import React, { createContext, useContext, useEffect, useState } from "react";
import { resolvePackageIncludes, CAR_TIER, UNIFORM_LOWER, UNIFORM_HIGHER, MERCH_LOWER, MERCH_HIGHER } from "@/lib/sponsorship";

const CartContext = createContext(null);
const STORAGE_KEY = "alight_shop_v2";
const LANG_KEY = "alight_lang";

export function CartProvider({ children }) {
  const [pkgId, setPkgId] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||"{}").pkgId || null; } catch { return null; } });
  const [benefitIds, setBenefitIds] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)||"{}").benefitIds || []; } catch { return []; } });
  const [lang, setLang] = useState(() => localStorage.getItem(LANG_KEY) || "en");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify({ pkgId, benefitIds })); }, [pkgId, benefitIds]);
  useEffect(() => { localStorage.setItem(LANG_KEY, lang); }, [lang]);

  const packageIncludes = pkgId ? resolvePackageIncludes(pkgId) : [];

  // When package changes, purge conflicts from selected extras
  const selectPackage = (id) => {
    setPkgId(id);
    const incl = resolvePackageIncludes(id);
    setBenefitIds(prev => prev.filter(b => {
      if (incl.includes(b)) return false; // already included
      // Uniform: if package brings benefit7, drop benefit2
      if (incl.includes(UNIFORM_HIGHER) && b === UNIFORM_LOWER) return false;
      // Merch: if package brings benefit16, drop benefit5
      if (incl.includes(MERCH_HIGHER) && b === MERCH_LOWER) return false;
      // Car: if package has a car benefit, drop lower/equal car extras
      const carInPkg = incl.find(x => CAR_TIER[x]);
      if (carInPkg && CAR_TIER[b] && CAR_TIER[b] <= CAR_TIER[carInPkg]) return false;
      return true;
    }));
  };
  const clearPackage = () => setPkgId(null);

  const canAddBenefit = (id) => {
    if (packageIncludes.includes(id)) return { ok:false, reason:"included" };
    if (packageIncludes.includes(UNIFORM_HIGHER) && id === UNIFORM_LOWER) return { ok:false, reason:"replaced" };
    if (packageIncludes.includes(MERCH_HIGHER) && id === MERCH_LOWER) return { ok:false, reason:"replaced" };
    const carInPkg = packageIncludes.find(x => CAR_TIER[x]);
    if (carInPkg && CAR_TIER[id] && CAR_TIER[id] <= CAR_TIER[carInPkg]) return { ok:false, reason:"replaced" };
    return { ok:true };
  };

  const toggleBenefit = (id) => {
    if (benefitIds.includes(id)) { setBenefitIds(benefitIds.filter(b => b !== id)); return { removed:true }; }
    const check = canAddBenefit(id);
    if (!check.ok) return check;
    // Also enforce single car branding among extras
    let next = benefitIds.filter(b => {
      if (CAR_TIER[id] && CAR_TIER[b]) return false; // remove other car brandings
      return true;
    });
    // Uniform: only one of benefit2/7 at a time among extras
    if (id === UNIFORM_LOWER || id === UNIFORM_HIGHER) next = next.filter(b => b !== UNIFORM_LOWER && b !== UNIFORM_HIGHER);
    // Merch: only one of benefit5/16 at a time among extras
    if (id === MERCH_LOWER || id === MERCH_HIGHER) next = next.filter(b => b !== MERCH_LOWER && b !== MERCH_HIGHER);
    setBenefitIds([...next, id]);
    return { ok:true };
  };

  const removeBenefit = (id) => setBenefitIds(benefitIds.filter(b => b !== id));
  const clear = () => { setPkgId(null); setBenefitIds([]); };

  const totalItems = (pkgId ? 1 : 0) + benefitIds.length;

  return (
    <CartContext.Provider value={{
      pkgId, benefitIds, packageIncludes, lang, setLang,
      selectPackage, clearPackage, toggleBenefit, removeBenefit, canAddBenefit, clear,
      totalItems, drawerOpen, setDrawerOpen,
      hasBenefit:(id)=>benefitIds.includes(id),
    }}>{children}</CartContext.Provider>
  );
}
export const useCart = () => useContext(CartContext);
